/**
 * Compute the aggregated metrics displayed on /candidates and /candidates/$slug.
 *
 * Composite score (client-side, no DB persistence):
 *   For each (chosen) architecture component:
 *     - weight w_k = component.criticality (1..5, default 3)
 *     - product_score_k = product's weighted total across manual criteria +
 *                         derived-coverage criteria for THIS product, using the
 *                         active weights preset.
 *     - if product is dealbreaker-failed: 0 numerator, full w_k denominator
 *     - if slot is TBD: 0 numerator, full w_k denominator
 *   composite = Σ(w_k × score_k) / Σ w_k
 *
 * Coverage at candidate level = best-fit union across all chosen products.
 * Native > Plugin > Custom > None. TBD slots = no coverage contribution.
 */

import { criticalityOf, architectureComponents } from "@/presentation/data/architecture-components";
import { computeProductScore } from "@/application/use-cases/products/compute-product-score";
import type {
  CandidateChoiceRow,
  EvaluationCriterionRow,
  FeatureProductCoverage,
  FeatureProductRow,
  FeatureRoadmapVerdictRow,
  FeatureRow,
  ProductRow,
  ProductScoreRow,
} from "@/infrastructure/supabase/database.types";

export interface CandidateAggregateInputs {
  choices: CandidateChoiceRow[];
  products: ProductRow[];
  productScores: ProductScoreRow[];
  criteria: EvaluationCriterionRow[];
  features: FeatureRow[];
  featureProducts: FeatureProductRow[];
  verdicts: FeatureRoadmapVerdictRow[];
  weights: Record<string, number>;
  /** Live criticality overrides ({ component_id: 1..5 }); baseline applies when absent. */
  criticalityOverrides?: Record<string, number>;
}

export interface DealbreakerOrigin {
  productId: string;
  productName: string;
  criterionId: string;
  criterionName: string;
}

export interface ComponentAggregate {
  componentId: string;
  productId: string | null;
  criticality: number;
  score: number | null; // null if TBD or dealbreaker fail
  isTBD: boolean;
  isDealbreakerFail: boolean;
  contribution: number; // criticality × score, or 0 for TBD/fail
}

export interface CandidateAggregateResult {
  composite: number | null;
  componentAggregates: ComponentAggregate[];
  totalCriticality: number;
  /** Number of components configured for this candidate (excludes the rest of the catalogue). */
  configuredComponentCount: number;
  /** Number of TBD slots among configured choices. */
  tbdCount: number;
  /** Origins of dealbreaker fails on chosen products. */
  dealbreakerOrigins: DealbreakerOrigin[];
  /** Provenance breakdown — counts per kind across chosen products. */
  provenance: {
    yangoReuse: number;
    offTheShelf: number;
    buildOnTop: number;
    moveoBuild: number;
    tbd: number;
  };
  /** Coverage breakdown per journey for this candidate's union of chosen products. */
  coverage: {
    b2c: CoverageBreakdown | null;
    b2bMvp: CoverageBreakdown | null;
    b2bNonMvp: CoverageBreakdown | null;
  };
}

export interface CoverageBreakdown {
  nativeCount: number;
  pluginCount: number;
  customCount: number;
  totalInScope: number;
  totalCovered: number;
  nativeRatio: number;
  pluginRatio: number;
  customRatio: number;
  totalRatio: number;
}

/**
 * Per-product weighted total — delegates to the shared computeProductScore so
 * /products, /products/$slug and the candidate composite can never diverge.
 */
function productWeightedTotal(
  product: ProductRow,
  inputs: CandidateAggregateInputs
): { total: number; hasDealbreaker: boolean } {
  const productScores = inputs.productScores.filter((s) => s.product_id === product.id);
  const productAssignments = inputs.featureProducts.filter((fp) => fp.product_id === product.id);
  const result = computeProductScore(productScores, productAssignments, {
    criteria: inputs.criteria,
    features: inputs.features,
    verdicts: inputs.verdicts,
    weights: inputs.weights,
  });
  return { total: result.weightedTotal, hasDealbreaker: result.hasDealbreaker };
}

// `irrelevant` rows do not contribute to candidate coverage — a feature
// marked irrelevant by the picked product is treated as if no row existed
// (rank 0). Another product in the candidate may still cover it.
const FIT_RANK: Record<FeatureProductCoverage, number> = {
  native: 3,
  plugin: 2,
  custom: 1,
  irrelevant: 0,
};

export function computeCandidateAggregate(
  inputs: CandidateAggregateInputs
): CandidateAggregateResult {
  const productById = new Map(inputs.products.map((p) => [p.id, p]));
  const componentById = new Map(architectureComponents.map((c) => [c.id, c]));

  // Pre-compute each product's weighted total + dealbreaker state (once).
  const productTotals = new Map<string, { total: number; hasDealbreaker: boolean }>();
  for (const p of inputs.products) {
    productTotals.set(p.id, productWeightedTotal(p, inputs));
  }

  const provenance = { yangoReuse: 0, offTheShelf: 0, buildOnTop: 0, moveoBuild: 0, tbd: 0 };
  const dealbreakerOrigins: DealbreakerOrigin[] = [];
  const componentAggregates: ComponentAggregate[] = [];
  let weightedSum = 0;
  let totalCriticality = 0;
  let tbdCount = 0;

  for (const choice of inputs.choices) {
    const component = componentById.get(choice.component_id);
    if (!component) continue;
    const w = criticalityOf(component, inputs.criticalityOverrides);
    totalCriticality += w;
    if (!choice.product_id) {
      tbdCount += 1;
      provenance.tbd += 1;
      componentAggregates.push({
        componentId: choice.component_id,
        productId: null,
        criticality: w,
        score: null,
        isTBD: true,
        isDealbreakerFail: false,
        contribution: 0,
      });
      continue;
    }
    const product = productById.get(choice.product_id);
    if (!product) continue;
    if (product.kind === "off-the-shelf") provenance.offTheShelf += 1;
    else if (product.kind === "build-on-top") provenance.buildOnTop += 1;
    else if (product.kind === "from-scratch") {
      // Differentiate Yango-reuse (vendor='Yango') from Moveo-build heuristically.
      if ((product.vendor ?? "").toLowerCase().includes("yango")) provenance.yangoReuse += 1;
      else provenance.moveoBuild += 1;
    }

    const totals = productTotals.get(product.id);
    const productScore = totals?.total ?? 0;
    if (totals?.hasDealbreaker) {
      // Surface dealbreaker origins
      const productDealbreakers = inputs.productScores.filter(
        (s) => s.product_id === product.id && s.dealbreaker_fail
      );
      for (const s of productDealbreakers) {
        const crit = inputs.criteria.find((c) => c.id === s.criterion_id);
        dealbreakerOrigins.push({
          productId: product.id,
          productName: product.name,
          criterionId: s.criterion_id,
          criterionName: crit?.name ?? s.criterion_id,
        });
      }
      componentAggregates.push({
        componentId: choice.component_id,
        productId: product.id,
        criticality: w,
        score: null,
        isTBD: false,
        isDealbreakerFail: true,
        contribution: 0,
      });
      continue;
    }
    weightedSum += w * productScore;
    componentAggregates.push({
      componentId: choice.component_id,
      productId: product.id,
      criticality: w,
      score: productScore,
      isTBD: false,
      isDealbreakerFail: false,
      contribution: w * productScore,
    });
  }

  const composite = totalCriticality > 0 ? weightedSum / totalCriticality : null;

  // Coverage at candidate level — best-fit union across chosen products
  const coverage = computeCandidateCoverage(inputs);

  return {
    composite,
    componentAggregates,
    totalCriticality,
    configuredComponentCount: inputs.choices.length,
    tbdCount,
    dealbreakerOrigins,
    provenance,
    coverage,
  };
}

function computeCandidateCoverage(
  inputs: CandidateAggregateInputs
): CandidateAggregateResult["coverage"] {
  const chosenProductIds = new Set(
    inputs.choices.map((c) => c.product_id).filter((id): id is string => id !== null)
  );
  // Best-fit per (feature) across all chosen products
  const bestFitByFeature = new Map<string, FeatureProductCoverage>();
  for (const fp of inputs.featureProducts) {
    if (!chosenProductIds.has(fp.product_id)) continue;
    if (fp.coverage === "irrelevant") continue;
    const current = bestFitByFeature.get(fp.feature_id);
    if (!current || FIT_RANK[fp.coverage] > FIT_RANK[current]) {
      bestFitByFeature.set(fp.feature_id, fp.coverage);
    }
  }

  const score = (filter: (f: FeatureRow) => boolean): CoverageBreakdown | null => {
    const inScope = inputs.features.filter(filter);
    if (inScope.length === 0) return null;
    let nativeCount = 0;
    let pluginCount = 0;
    let customCount = 0;
    for (const f of inScope) {
      const fit = bestFitByFeature.get(f.id);
      if (fit === "native") nativeCount += 1;
      else if (fit === "plugin") pluginCount += 1;
      else if (fit === "custom") customCount += 1;
    }
    const totalInScope = inScope.length;
    const totalCovered = nativeCount + pluginCount + customCount;
    return {
      nativeCount,
      pluginCount,
      customCount,
      totalInScope,
      totalCovered,
      nativeRatio: nativeCount / totalInScope,
      pluginRatio: pluginCount / totalInScope,
      customRatio: customCount / totalInScope,
      totalRatio: totalCovered / totalInScope,
    };
  };

  const b2c = score((f) => f.journey === "B2C" || f.journey === "both");
  const verdictsByFeature = new Map<string, FeatureRoadmapVerdictRow[]>();
  for (const v of inputs.verdicts) {
    const arr = verdictsByFeature.get(v.feature_id) ?? [];
    arr.push(v);
    verdictsByFeature.set(v.feature_id, arr);
  }
  const isMvp = (id: string) =>
    (verdictsByFeature.get(id) ?? []).some(
      (v) => v.branch === "all-features" && v.verdict === "mvp"
    );
  const isNonMvp = (id: string) =>
    (verdictsByFeature.get(id) ?? []).some(
      (v) => v.branch === "all-features" && v.verdict === "non-mvp"
    );
  const b2bMvp = score((f) => (f.journey === "B2B" || f.journey === "both") && isMvp(f.id));
  const b2bNonMvp = score((f) => (f.journey === "B2B" || f.journey === "both") && isNonMvp(f.id));
  return { b2c, b2bMvp, b2bNonMvp };
}
